// Right sidebar History pane: revision list, revision preview, restore.
import { api } from './api.js';
import { clear, formatBytes, formatTime, h } from './dom.js';
import { confirmDialog, openModal, toast } from './modals.js';
import type { HistoryEntry } from './types.js';
import type { App } from './app.js';

function decodeUtf8(bytes: Uint8Array): { text: string; binary: boolean } {
  if (bytes.some((byte) => byte === 0)) return { text: '', binary: true };
  try {
    return { text: new TextDecoder('utf-8', { fatal: true }).decode(bytes), binary: false };
  } catch {
    return { text: '', binary: true };
  }
}

async function previewRevision(path: string, entry: HistoryEntry): Promise<void> {
  const modal = openModal({ title: `${path} @ rev ${entry.rev}`, wide: true });
  const holder = h('div', { class: 'revision-preview' });
  modal.body.append(
    h('div', { class: 'revision-meta' }, [
      h('span', { text: `device ${entry.device}` }),
      h('span', { text: formatTime(entry.ts) }),
      h('span', { text: formatBytes(entry.size) }),
    ]),
    holder,
  );
  if (!entry.hash) {
    holder.append(h('p', { class: 'empty-note', text: 'This revision deleted the file — there is no content.' }));
    return;
  }
  try {
    const bytes = await api.historyBlob(entry.hash);
    const decoded = decodeUtf8(bytes);
    if (decoded.binary) {
      holder.append(h('p', { class: 'empty-note', text: `Binary revision (${formatBytes(entry.size)})` }));
    } else {
      holder.append(h('pre', { class: 'revision-text', text: decoded.text }));
    }
  } catch (error) {
    holder.append(h('p', { class: 'empty-note', text: `Revision unavailable: ${(error as Error).message}` }));
  }
}

/** Revision list for the currently open note. */
export class HistoryPane {
  readonly el: HTMLElement;
  private readonly list: HTMLElement;
  private readonly app: App;

  constructor(app: App) {
    this.app = app;
    this.list = h('div', { class: 'history-list' });
    this.el = h('div', { class: 'history-pane' }, [this.list]);
    this.setEmpty('Open a note to see its revisions.');
  }

  private setEmpty(text: string): void {
    clear(this.list);
    this.list.append(h('p', { class: 'empty-note', text }));
  }

  async refresh(): Promise<void> {
    const path = this.app.current;
    if (!path) {
      this.setEmpty('Open a note to see its revisions.');
      return;
    }
    try {
      const response = await api.history(path);
      this.render(path, response.history);
    } catch (error) {
      this.setEmpty(`History unavailable: ${(error as Error).message}`);
    }
  }

  private render(path: string, history: HistoryEntry[]): void {
    clear(this.list);
    if (history.length === 0) {
      this.list.append(h('p', { class: 'empty-note', text: 'No revisions recorded yet.' }));
      return;
    }
    for (const entry of history) {
      const view = h('button', {
        class: 'ghost-button small',
        text: 'View',
        type: 'button',
        on: { click: () => void previewRevision(path, entry) },
      });
      const restore = h('button', {
        class: 'ghost-button small',
        text: 'Restore',
        type: 'button',
        on: {
          click: () => {
            void (async () => {
              if (!entry.hash) return;
              const ok = await confirmDialog(
                'Restore revision',
                `Restore “${path}” to revision ${entry.rev} (${formatTime(entry.ts)})?`,
                'Restore',
              );
              if (!ok) return;
              try {
                await api.restore(path, entry.hash);
                toast(`Restored ${path} to rev ${entry.rev}`, { kind: 'success' });
                await this.app.reloadCurrent();
                await this.app.vaultChanged();
                await this.refresh();
              } catch (error) {
                toast(`Restore failed: ${(error as Error).message}`, { kind: 'error' });
              }
            })();
          },
        },
      });
      this.list.append(
        h('div', { class: `history-row${entry.deleted ? ' deleted' : ''}` }, [
          h('div', { class: 'history-head' }, [
            h('span', { class: 'history-rev', text: `rev ${entry.rev}` }),
            h('span', { class: 'history-device', text: entry.device || 'unknown' }),
          ]),
          h('div', { class: 'history-meta', text: `${formatTime(entry.ts)} · ${entry.deleted ? 'deleted' : formatBytes(entry.size)}` }),
          h('div', { class: 'history-actions' }, [view, restore]),
        ]),
      );
    }
  }
}
